import NativeOperationControls from '../../components/Apps/NativeOperationControls';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp, Home, Undo2 } from 'lucide-react';
import UnifiedTvControl from '../../components/Controls/UnifiedTvControl';
import { UNIFIED_TV_COMMANDS } from '../../components/Controls/unifiedTvControlCommands';
import KaraokeRemotePanel from '../../components/Remote/KaraokeRemotePanel';
import RemotePwaSupport from '../../components/Remote/RemotePwaSupport';
import useSharedRemoteRuntime from '../../components/Remote/useSharedRemoteRuntime';
import RemoteScheduleTimeline from './RemoteScheduleTimeline';
import VenueDashboard from './VenueDashboard';
import { refreshVenueInputReports } from './venueInputReports';
import RemoteMediaLinkPanel from './RemoteMediaLinkPanel';
import LauncherPanels, { LauncherPanel } from './LauncherPanels';
import LauncherAdsPanel from './LauncherAdsPanel';
import RemoteEpg, { epgAdDevice } from './RemoteEpg';
import RemoteEpgSearch from './RemoteEpgSearch';
import useSiteOperation from '../../components/SiteControl/useSiteOperation';
import RemoteSitePowerControls from '../../components/Remote/RemoteSitePowerControls';
import useScreenOperation from '../../components/SiteControl/useScreenOperation';
import RemotePowerDiagnostics from '../../components/Remote/RemotePowerDiagnostics';
import RemotePhoneOnboarding from '../../components/Remote/RemotePhoneOnboarding';
import { prepareScreenSource, waitForSourceActivation } from './screenRoutineLaunch';
import {
  getRemoteTarget,
  getRemoteAssignedDestinations,
  executeRemoteAssignedApp,
  getRemoteAssignedOperation,
  continueRemoteAssignedOperation,
  cancelRemoteAssignedOperation,
  remoteScreenOperationApi,
  remoteSiteOperationApi,
  getRemoteVenueSummary,
  launchRemoteJukeboxRuntime,
  launchRemoteKaraokeRuntime,
  listRemoteLiveExperienceInstances,
  cancelRemoteIdentificationScan,
  completeRemoteIdentificationScan,
  listRemoteContentTargets,
  listRemoteAreaLauncherContent,
  listRemoteLauncherAds,
  mixRemoteTargetNow,
  launchRemoteLauncherAd,
  clearRemoteLauncherAds,
  executeRemoteAreaPlaylist,
  previewRemoteMediaLink,
  executeRemoteContentTarget,
  requestRemoteState,
  runRemoteMediaLinkAction,
  saveRemoteMediaToAreaPlaylist,
  sendRemoteAssistantCommand,
  sendRemoteAndroidTvAction,
  openRemoteAndroidBluetoothSettings,
  sendRemoteProtocolCommand,
  sendRemoteRoutedAction,
  sendRemoteSocketCommand,
  startRemoteIdentificationScan,
  stopRemoteJukeboxRuntime,
  stopRemoteKaraokeRuntime,
} from '../../components/Remote/deviceRemoteApi';
import useRemoteSurfaceSession, {
  COMMAND_RESPONSE_TYPES,
  buildSyntheticResponse,
  createLocalEmitter,
  formatTargetOption,
  normalizeInstalledApps,
} from '../../components/Remote/useRemoteSurfaceSession';
import RemoteCommandFeedback from '../../components/Remote/RemoteCommandFeedback';
import {
  getRemoteNavigationItems,
  getRemotePageIndex,
  getRemotePageForOrientation,
  getRemotePortraitPage,
  isRemoteLandscape,
  keepRemoteInputVisible,
} from './remoteLayout';
import { getSelectedActiveLiveExperience } from './liveExperienceSelection';
import { buildSharedScreenTargets, buildTargetTree, parseHdmiPort, resolveScreenContext } from './launcherTopology';
import { prepareMediaPlayback, getMediaPlaybackOptions } from './mediaPlaybackRouting';
import { loadLauncherDeviceInventory } from './launcherDeviceInventory';
import './remoteApp.css';

function RemoteApp() {
  const emitterRef = useRef(createLocalEmitter());
  const screensRef = useRef(null);
  const venueScreenRef = useRef(null);
  const epgScreenRef = useRef(null);
  const launcherScreenRef = useRef(null);
  const scannerVideoRef = useRef(null);
  const scannerControlsRef = useRef(null);
  const scannerSessionRef = useRef('');
  const scannerCompletingRef = useRef(false);
  const scannerSnapshottingRef = useRef(false);
  const suitestreamScreenRef = useRef(null);
  const controlsScreenRef = useRef(null);
  const isLandscapeRef = useRef(false);
  const navigationAlignmentRef = useRef(false);
  const viewportWidthRef = useRef(0);
  const activePortraitPageRef = useRef('venue');
  const [activePortraitPage, setActivePortraitPage] = useState('venue');
  const [epgNavigation, setEpgNavigation] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [targetMenuOpen, setTargetMenuOpen] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scannerStatus, setScannerStatus] = useState('idle');
  const [scannerMessage, setScannerMessage] = useState('');
  const [scannerZoom, setScannerZoom] = useState(null);
  const [scannerZoomRange, setScannerZoomRange] = useState(null);
  const [browseSiteKey, setBrowseSiteKey] = useState('');
  const [browseAreaKey, setBrowseAreaKey] = useState('');
  const [isLandscape, setIsLandscape] = useState(false);
  const activeNavigation = getRemotePageForOrientation(activePortraitPage, isLandscape);
  const [viewportHeight, setViewportHeight] = useState(null);
  const [mediaLinkInput, setMediaLinkInput] = useState('');
  const [mediaLinkStatus, setMediaLinkStatus] = useState('idle');
  const [mediaLinkPreview, setMediaLinkPreview] = useState(null);
  const [mediaLinkMessage, setMediaLinkMessage] = useState('');
  const [mediaLinkBusyChoice, setMediaLinkBusyChoice] = useState('');
  const [liveExperienceInstances, setLiveExperienceInstances] = useState([]);
  const [liveExperienceStatus, setLiveExperienceStatus] = useState('loading');
  const [liveExperienceMessage, setLiveExperienceMessage] = useState('');
  const [liveExperienceBusyId, setLiveExperienceBusyId] = useState(null);
  const [launcherDevices, setLauncherDevices] = useState([]);
  const [areaLauncherContent, setAreaLauncherContent] = useState({ playlists: [], contentTargets: [] });
  const [launcherRefresh, setLauncherRefresh] = useState(0);
  const [launcherAds, setLauncherAds] = useState({ adPlaylists: [], campaigns: [], socialPosts: [] });
  const [launcherAdsStatus, setLauncherAdsStatus] = useState('loading');
  const [launcherAdsMessage, setLauncherAdsMessage] = useState('');
  const [launcherStatus, setLauncherStatus] = useState('loading');
  const [launcherMessage, setLauncherMessage] = useState('');
  const [launcherBusyKey, setLauncherBusyKey] = useState('');
  const [assignedOperation, setAssignedOperation] = useState(null);
  const [epgBusy, setEpgBusy] = useState(false);
  const [targetMenuScreens, setTargetMenuScreens] = useState([]);
  const [targetMenuStates, setTargetMenuStates] = useState({});
  const [targetMenuNow, setTargetMenuNow] = useState(Date.now);
  const [screenContextId, setScreenContextId] = useState(null);
  const [routineSummary, setRoutineSummary] = useState(null);
  const launcherInFlight = useRef(false);
  const {
    activeTarget,
    bootMessage,
    bootStatus,
    device,
    remoteDevice,
    selectedTargetId,
    selectTarget,
    setDevice,
    targetLoading,
    targets,
  } = useRemoteSurfaceSession({
    expectedRoute: '/remote',
    targetStorageKeyPrefix: 'phoneRemote',
    loadingMessage: 'Loading your phone remote…',
    errorMessage: 'Failed to load your phone remote.',
    emptyMessage: 'No controllable devices are available on this account yet.',
  });

  const dispatchSocketCommand = useCallback(({ deviceId, command, value, id, requestId }) => {
    sendRemoteSocketCommand({
      targetDeviceId: deviceId,
      command,
      value,
      requestId,
    })
      .then((data) => {
        emitterRef.current.emitResponse(
          buildSyntheticResponse({
            deviceId,
            command,
            value,
            requestId: id,
            data,
          })
        );
      })
      .catch((error) => {
        if (error?.status === 404 || error?.status === 409) {
          emitterRef.current.emitMissing({
            deviceId,
            requestId: id,
            command,
            reason: error.status === 409 ? 'native_channel_missing' : 'device_not_connected',
            message: error.message,
          });
          return;
        }

        emitterRef.current.emitResponse({
          deviceId,
          type: COMMAND_RESPONSE_TYPES[command] || `${command}_response`,
          requestId: id,
          success: false,
          error: error.message || 'Command failed',
        });
      });

    return id;
  }, []);

  const transport = useMemo(() => ({
    requestState: async ({ deviceId, includeScreenshot }) => {
      const data = await requestRemoteState({
        targetDeviceId: deviceId,
        includeScreenshot,
      });
      return data?.state || null;
    },
    loadApps: async ({ deviceId }) => {
      const data = await getRemoteTarget(deviceId, { includeApps: true });
      return normalizeInstalledApps(data?.target?.installedApps || []);
    },
    loadContentTargets: async ({ deviceId }) => {
      const data = await listRemoteContentTargets(deviceId);
      return data?.contentTargets || [];
    },
    executeContentTarget: ({ deviceId, contentTargetId }) =>
      executeRemoteContentTarget(deviceId, contentTargetId),
    sendProtocolCommand: async ({ deviceId, command, protocol, requestId }) => {
      const data = await sendRemoteProtocolCommand({
        targetDeviceId: deviceId,
        command,
        protocol,
        requestId,
      });
      return data;
    },
    sendAssistantCommand: async ({ deviceId, command, params, requestId }) => {
      const data = await sendRemoteAssistantCommand({
        targetDeviceId: deviceId,
        command,
        params,
        requestId,
      });
      return { ...data, response: data?.response || '' };
    },
    sendRoutedAction: async ({ deviceId, ...payload }) =>
      sendRemoteRoutedAction({ targetDeviceId: deviceId, ...payload }),
    sendAndroidTvRemoteAction: ({ deviceId, ...payload }) =>
      sendRemoteAndroidTvAction({ targetDeviceId: deviceId, ...payload }),
    openBluetoothSettings: ({ deviceId }) => openRemoteAndroidBluetoothSettings({ targetDeviceId: deviceId }),
    sendSocketCommand: ({ deviceId, command, value, id, requestId }) =>
      dispatchSocketCommand({ deviceId, command, value, id, requestId }),
    subscribe: ({ onResponse, onMissing }) =>
      emitterRef.current.subscribe({ onResponse, onMissing }),
  }), [dispatchSocketCommand]);

  const {
    canPreviewSnapshot,
    hdmiSources,
    tvHomeSource,
    handleCommand,
    handlePrimaryRemoteCommand,
    handleRemoteInputEnd,
    fetchLiveSnapshot,
    handleWatchSource,
    liveSnapshot,
    loadContentTargets,
    remoteAvailableCommands,
    snapshotMessage,
    snapshotStatus,
    commandAttemptHistory,
    lastCommandAttempt,
    retryLastCommand,
    selectedMethod,
    supportsPreviewSnapshot,
    watchTarget,
  } = useSharedRemoteRuntime({
    device,
    onDeviceUpdated: setDevice,
    onSourceTargetSelected: selectTarget,
    transport,
    previewEnabled:
      activeNavigation === 'remote' && (previewOpen || isLandscape),
    previewBackgroundIntervalOverride:
      isLandscape && activeNavigation === 'remote' ? 1000 : null,
  });

  const previewImageUrl = liveSnapshot?.screenshot?.dataUrl || '';
  const routinesEnabled = routineSummary?.areaId === Number(activeTarget?.areaId) && routineSummary?.screenOperationsEnabled === true;
  const screenModePending = routineSummary?.areaId !== Number(activeTarget?.areaId);
  const routineScreen = routinesEnabled ? resolveScreenContext(routineSummary.screens, selectedTargetId, screenContextId) : null;
  const routineBase = `/areas/${activeTarget?.areaId}/screens/${routineScreen?.id}/operations`;
  const screenExecution = useScreenOperation(activeTarget?.areaId, routineScreen?.id, {
    api: remoteScreenOperationApi, basePath: routineBase, storagePrefix: 'remote:',
  });
  const routineSiteId = Number(activeTarget?.siteId) || null;
  const siteExecution = useSiteOperation(routineSiteId, {
    api: remoteSiteOperationApi,
    executeBasePath: `/sites/${routineSiteId}/power-routines`,
    operationBasePath: `/sites/${routineSiteId}/power-operations`,
    storagePrefix: 'remote:',
  });
  const siteRoutinesEnabled = routineSiteId && routineSummary?.venue?.siteId === routineSiteId && routineSummary?.siteOperationsEnabled === true;
  const powerRoutineBusy = screenExecution.busy || siteExecution.busy || epgBusy;
  const selectionRef = useRef('');
  selectionRef.current = `${activeTarget?.areaId}:${routineScreen?.id}:${selectedTargetId}`;
  React.useEffect(() => () => { selectionRef.current = ''; }, []);
  const screenWatchSource = routineScreen?.players?.find((player) => Number(player.deviceId || player.id) === Number(selectedTargetId))
    || routineScreen?.players?.find((player) => Number(player.deviceId || player.id) === Number(routineScreen.preferredPlayerDeviceId));
  const renderPowerRoutinePanel = () => <>
    <RemoteSitePowerControls siteName={activeTarget?.siteName} enabled={Boolean(siteRoutinesEnabled)}
      blocked={screenExecution.busy || epgBusy || Boolean(launcherBusyKey)} execution={siteExecution} />
    {routinesEnabled && <section className="phone-remote__screen-routines" aria-label="Screen routines">
    <strong>{routineScreen?.name || 'Choose a Screen'}</strong>
    <p>{routineScreen ? 'Controls this TV and its configured sources together. Shared players and wake relays are protected.' : 'Select the player under the intended Screen in the target menu. Shared sources are never assigned to the first TV automatically.'}</p>
    <div className="phone-remote__compact-actions">
      <button type="button" className="phone-remote__action-btn" disabled={!routineScreen || powerRoutineBusy || Boolean(launcherBusyKey)} onClick={() => screenExecution.run('start')}>Start Screen</button>
      <button type="button" className="phone-remote__action-btn phone-remote__action-btn--warn" disabled={!routineScreen || powerRoutineBusy || Boolean(launcherBusyKey)} onClick={() => screenExecution.run('stop')}>Stop Screen</button>
    </div>
    <details className="phone-remote__device-diagnostics"><summary>Advanced · device-only power</summary>
      <p>Only {device?.name || 'this device'}, not the whole Screen. A toggle can wake or sleep it; check its physical state first.</p>
      <button type="button" className="phone-remote__action-btn phone-remote__action-btn--warn" disabled={powerRoutineBusy || Boolean(launcherBusyKey)} onClick={() => handlePrimaryRemoteCommand('power')}>Device power toggle</button>
    </details>
  </section>}
    <details className="phone-remote__device-diagnostics"><summary>Power diagnostics & phone setup</summary>
      <RemotePowerDiagnostics siteName={activeTarget?.siteName} screenName={routineScreen?.name}
        siteExecution={siteExecution} screenExecution={screenExecution} />
      <RemotePhoneOnboarding areaId={activeTarget?.areaId || remoteDevice?.areaId}
        areaName={activeTarget?.areaName || remoteDevice?.areaName}
        siteName={activeTarget?.siteName || remoteDevice?.siteName} />
    </details>
  </>;

  React.useEffect(() => {
    if (!selectedTargetId || !activeTarget?.areaId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        setLauncherStatus('loading');
        setLauncherMessage('');
        const [summaryResult, areaContentResult] = await Promise.allSettled([
          getRemoteVenueSummary(activeTarget.areaId),
          listRemoteAreaLauncherContent(activeTarget.areaId),
        ]);
        if (summaryResult.status === 'rejected') throw summaryResult.reason;
        const summary = summaryResult.value;
        if (summary.sources?.topology?.status === 'unavailable') throw new Error('Screen connections could not be loaded. Refresh the launcher to retry.');
        if (!cancelled) setRoutineSummary({ ...summary, areaId: Number(activeTarget.areaId) });
        const routes = buildSharedScreenTargets({
          screens: summary?.screens || [],
          targets,
          selectedDeviceId: selectedTargetId,
          selectedScreenId: screenContextId,
          requireScreenContext: summary.screenOperationsEnabled === true,
        });
        const devices = await Promise.all(routes.map((route) => loadLauncherDeviceInventory(route, {
          getTarget: getRemoteTarget, getLinks: listRemoteContentTargets, getAssigned: getRemoteAssignedDestinations,
        })));
        if (!cancelled) {
          setAreaLauncherContent(areaContentResult.status === 'fulfilled'
            ? areaContentResult.value : { playlists: [], contentTargets: [] });
          setLauncherDevices(devices);
          setLauncherStatus('ready');
          if (areaContentResult.status === 'rejected') setLauncherMessage('Saved playlists could not be loaded. Refresh the launcher to retry.');
          if (!routes.length) setLauncherMessage('Choose the Screen this source should play on in the device selector.');
        }
      } catch (error) {
        if (!cancelled) {
          setAreaLauncherContent({ playlists: [], contentTargets: [] });
          setLauncherDevices([]);
          setLauncherStatus('error');
          setLauncherMessage(error.message || 'Could not load apps for this TV.');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [activeTarget?.areaId, selectedTargetId, targets, screenContextId, launcherRefresh]);

  React.useEffect(() => {
    if (!selectedTargetId) return undefined;
    let cancelled = false;
    setLauncherAdsStatus('loading');
    setLauncherAdsMessage('');
    setLauncherAds({ adPlaylists: [], campaigns: [], socialPosts: [] });
    listRemoteLauncherAds(selectedTargetId).then((data) => {
      if (!cancelled) { setLauncherAds(data); setLauncherAdsStatus('ready'); }
    }).catch((error) => {
      if (!cancelled) { setLauncherAdsStatus('error'); setLauncherAdsMessage(error.message || 'Could not load Ads.'); }
    });
    return () => { cancelled = true; };
  }, [selectedTargetId, launcherRefresh, activeNavigation]);

  const prepareLauncherDevice = useCallback(async (launcherDevice) => {
    const targetDeviceId = Number(launcherDevice?.target?.id);
    if (!targetDeviceId) throw new Error('The launch device is unavailable.');
    if (routinesEnabled && launcherDevice.screen) {
      if (!routineScreen || Number(launcherDevice.screen?.id) !== Number(routineScreen.id)) throw new Error('Choose the Screen this source should play on.');
      const selection = selectionRef.current;
      await prepareScreenSource({
        screen: routineScreen,
        targetDeviceId,
        readState: async (televisionDeviceId) => (await requestRemoteState({
          targetDeviceId: televisionDeviceId, includeScreenshot: false,
        }))?.state,
        stillSelected: () => selectionRef.current === selection,
        activate: async () => {
          setLauncherMessage(`Activating ${launcherDevice.target.name} on ${routineScreen.name}…`);
          const operation = await screenExecution.run('activate_source', targetDeviceId);
          await waitForSourceActivation({ operation,
            read: async (id) => (await remoteScreenOperationApi.get(`${routineBase}/${id}`)).data,
            stillSelected: () => selectionRef.current === selection });
        },
      });
      return targetDeviceId;
    }
    if (launcherDevice.switchRequired) {
      const televisionDeviceId = Number(launcherDevice.screen?.televisionDeviceId);
      const hdmiPort = parseHdmiPort(launcherDevice.expectedInput);
      if (!televisionDeviceId || !hdmiPort) {
        throw new Error('Set up and validate this player’s TV input in Site Controls first.');
      }
      setLauncherMessage(`Switching ${launcherDevice.screen?.name || 'TV'} to ${launcherDevice.target.name}…`);
      await sendRemoteAndroidTvAction({
        targetDeviceId: televisionDeviceId,
        action: `input_hdmi_${hdmiPort}`,
        configuredRoute: true,
        repeat: 1,
        intervalMs: 35,
      });
    }
    return targetDeviceId;
  }, [routinesEnabled, routineScreen, screenExecution, routineBase]);

  const confirmLauncherDevice = useCallback((launcherDevice, selection) => prepareMediaPlayback({
    device: launcherDevice,
    readState: async (targetDeviceId) => (await requestRemoteState({ targetDeviceId, includeScreenshot: false }))?.state,
    stillSelected: () => selectionRef.current === selection,
    confirm: (warning) => window.confirm(warning),
    prepare: prepareLauncherDevice,
  }), [prepareLauncherDevice]);

  const launchFromSharedScreen = useCallback(async (launcherDevice, item) => {
    if (launcherInFlight.current || powerRoutineBusy || launcherStatus !== 'ready' || screenModePending) return;
    launcherInFlight.current = true;
    const busyKey = `${launcherDevice?.target?.id}:${item.kind}:${item.id || item.packageName}`;
    try {
      setLauncherBusyKey(busyKey);
      setLauncherMessage('');
      const selection = selectionRef.current;
      if (item.kind === 'assigned') {
        const deviceId = Number(launcherDevice.target.id);
        if (item.shared && !window.confirm('Changing this box affects every Screen sharing it. Continue?')) return;
        let operation = await executeRemoteAssignedApp(deviceId, item.id, Boolean(item.shared));
        setAssignedOperation({ ...operation, deviceId });
        sessionStorage.setItem(`phone-app-operation:${selectedTargetId}`, JSON.stringify({ id: operation.id, deviceId }));
        const deadline = Date.now() + 370000;
        while (['pending', 'running'].includes(operation.status) && Date.now() < deadline) {
          await new Promise((resolve) => setTimeout(resolve, 1000));
          operation = await getRemoteAssignedOperation(deviceId, operation.id);
          if (selectionRef.current === selection) setAssignedOperation({ ...operation, deviceId });
        }
        if (!['completed_unverified', 'verified'].includes(operation.status)) throw new Error(operation.message || 'App destination did not complete. Check its setup before retrying.');
        setLauncherMessage(operation.result?.message || 'App destination prepared; check the picture.');
        return;
      }
      if (!await confirmLauncherDevice(launcherDevice, selection)) return;
      const targetDeviceId = Number(launcherDevice.target.id);
      if (selectionRef.current !== selection) throw new Error('Selection changed; content was not launched.');
      if (['campaign', 'ad_playlist', 'social_post'].includes(item.kind)) {
        await launchRemoteLauncherAd(targetDeviceId, { kind: item.kind, id: item.id, slot: item.slot, pauseBackground: item.pauseBackground, logicalScreenId: launcherDevice.screen?.id });
      } else {
        await clearRemoteLauncherAds({ targetDeviceId, logicalScreenId: launcherDevice.screen?.id });
        if (item.kind === 'mix_now') {
          await mixRemoteTargetNow(launcherDevice.appControlDeviceId || targetDeviceId);
        } else if (item.kind === 'playlist') {
          await executeRemoteAreaPlaylist(targetDeviceId, item.id);
        } else if (item.kind === 'link') {
          await executeRemoteContentTarget(targetDeviceId, item.id);
        } else {
          await sendRemoteSocketCommand({
            targetDeviceId,
            command: item.kind === 'suitestream' ? 'launch_suitestream' : 'launch_app',
            value: item.kind === 'app' ? { package: item.packageName } : null,
          });
        }
      }
      if (launcherDevice.switchRequired) await selectTarget(targetDeviceId);
      setLauncherMessage(`${item.label} launched on ${launcherDevice.target.name}.`);
    } catch (error) {
      setLauncherMessage(error.message || `Could not launch ${item.label}.`);
    } finally {
      launcherInFlight.current = false;
      setLauncherBusyKey('');
    }
  }, [selectedTargetId, confirmLauncherDevice, powerRoutineBusy, launcherStatus, screenModePending, selectTarget]);

  const discoverMediaInput = async (rawInput) => {
    const input = String(rawInput || '').trim();
    if (!input || !selectedTargetId) return;
    try {
      setMediaLinkStatus('loading');
      setMediaLinkMessage('');
      setMediaLinkPreview(null);
      const result = await previewRemoteMediaLink({
        targetDeviceId: selectedTargetId,
        input,
      });
      const choices = Array.isArray(result.choices) ? result.choices : [];
      if (result.mode === 'search') {
        setMediaLinkPreview({
          choices,
          query: result.query,
          results: Array.isArray(result.results) ? result.results : [],
        });
        if (!result.results?.length) setMediaLinkMessage('No YouTube results found.');
      } else {
        setMediaLinkPreview({ media: result.media, choices });
        if (!choices.length && result.media?.provider !== 'youtube') {
          setMediaLinkMessage('No TV in this area has a compatible playback app.');
        }
      }
      setMediaLinkStatus('ready');
    } catch (error) {
      setMediaLinkStatus('error');
      setMediaLinkMessage(error.message || 'This link or search could not be loaded.');
    }
  };

  const handleMediaLinkPreview = () => discoverMediaInput(mediaLinkInput);

  const handlePasteMediaLink = async () => {
    if (mediaLinkInput.trim()) {
      await discoverMediaInput(mediaLinkInput);
      return;
    }
    try {
      const value = await navigator.clipboard.readText();
      if (!value.trim()) {
        setMediaLinkMessage('The clipboard is empty. Type a YouTube search instead.');
        return;
      }
      const pasted = value.trim();
      setMediaLinkInput(pasted);
      await discoverMediaInput(pasted);
    } catch {
      setMediaLinkMessage('Clipboard access was blocked. Press and hold the field to paste.');
    }
  };

  const handleSelectMediaSearchResult = (media) => {
    if (!media) return;
    setMediaLinkInput(media.canonicalUrl);
    setMediaLinkPreview((current) => ({
      choices: current?.choices || [],
      media,
    }));
    setMediaLinkMessage('');
    setMediaLinkStatus('ready');
  };

  const handleSaveAreaPlaylist = async ({ areaId }) => {
    if (!mediaLinkPreview?.media?.canonicalUrl || !areaId) return;
    try {
      setMediaLinkBusyChoice('playlist:save_area');
      setMediaLinkMessage('');
      const result = await saveRemoteMediaToAreaPlaylist({
        areaId,
        url: mediaLinkPreview.media.canonicalUrl,
      });
      setLauncherRefresh((value) => value + 1);
      setMediaLinkStatus('ready');
      setMediaLinkInput('');
      setMediaLinkMessage(
        `${result.created ? 'Saved' : 'Updated'} “${result.name}” with ${result.itemCount} video${result.itemCount === 1 ? '' : 's'} for this area.`
      );
    } catch (error) {
      setMediaLinkStatus('error');
      setMediaLinkMessage(error.message || 'Could not save this Suitestream playlist.');
    } finally {
      setMediaLinkBusyChoice('');
    }
  };

  const prepareMediaDevice = async (device) => {
    if (!device || powerRoutineBusy || screenModePending || launcherStatus !== 'ready') {
      throw new Error('Wait for screen controls to be ready before playing.');
    }
    const selection = selectionRef.current;
    return confirmLauncherDevice(device, selection);
  };

  const handleMediaLinkAction = async (choice, action, device) => {
    if (!choice || !mediaLinkPreview?.media || mediaLinkBusyChoice || launcherInFlight.current) return;
    const busyKey = `${choice.profileId}:${action}`;
    launcherInFlight.current = true;
    try {
      setMediaLinkBusyChoice(busyKey);
      setMediaLinkMessage('');
      if (action === 'open' && !await prepareMediaDevice(device)) return;
      const result = await runRemoteMediaLinkAction({
        targetDeviceId: selectedTargetId,
        profileId: choice.profileId,
        url: mediaLinkPreview.media.canonicalUrl,
        title: mediaLinkPreview.media.title,
        action,
      });
      setMediaLinkInput('');
      if (action === 'open') {
        if (String(result.targetDeviceId) !== String(selectedTargetId)) {
          await selectTarget(device.target.id);
        }
        setMediaLinkStatus('ready');
        setMediaLinkMessage(`Opened with ${result.appName} on ${result.targetDeviceName}.`);
      } else {
        await loadContentTargets();
        setLauncherRefresh((value) => value + 1);
        setMediaLinkStatus('ready');
        if (action === 'save_area') {
          setMediaLinkMessage(
            `Saved ${result.name} to ${result.compatibleDeviceCount} compatible screen${result.compatibleDeviceCount === 1 ? '' : 's'} in this area.`
          );
        } else {
          setMediaLinkMessage(
            result.created
              ? `Saved ${result.name} for ${result.targetDeviceName}.`
              : `${result.name} is already saved for ${result.targetDeviceName}.`
          );
        }
      }
    } catch (error) {
      setMediaLinkStatus('error');
      setMediaLinkMessage(error.message || 'Media link action failed.');
    } finally {
      launcherInFlight.current = false;
      setMediaLinkBusyChoice('');
    }
  };

  const handleMediaSuitestreamPlay = async ({ areaId, device }) => {
    if (!mediaLinkPreview?.media?.canonicalUrl || !areaId || mediaLinkBusyChoice || launcherInFlight.current) return;
    const selection = selectionRef.current;
    launcherInFlight.current = true;
    try {
      setMediaLinkBusyChoice('suitestream:play');
      setMediaLinkMessage('');
      if (!await prepareMediaDevice(device)) return;
      const saved = await saveRemoteMediaToAreaPlaylist({
        areaId,
        url: mediaLinkPreview.media.canonicalUrl,
      });
      setMediaLinkInput('');
      setLauncherRefresh((value) => value + 1);
      if (selectionRef.current !== selection) throw new Error('Selection changed; the playlist was saved but playback was not launched.');
      await executeRemoteAreaPlaylist(device.target.id, saved.playlistId);
      if (String(device.target.id) !== String(selectedTargetId)) await selectTarget(device.target.id);
      setMediaLinkStatus('ready');
      setMediaLinkMessage(`Playing “${saved.name}” in Suitestream on ${device.target.name}.`);
    } catch (error) {
      setMediaLinkStatus('error');
      setMediaLinkMessage(error.message || 'Could not play this Suitestream playlist.');
    } finally {
      launcherInFlight.current = false;
      setMediaLinkBusyChoice('');
    }
  };

  const targetAreaKey = useCallback((target) => {
    const siteKey = String(target?.siteId || target?.siteName || 'site');
    const areaKey = String(target?.areaId || target?.areaName || 'area');
    return `${siteKey}:${areaKey}`;
  }, []);

  const targetSiteKey = useCallback(
    (target) => targetAreaKey(target).split(':')[0],
    [targetAreaKey]
  );

  const siteOptions = useMemo(() => {
    const options = new Map();
    targets.forEach((target) => {
      const key = targetSiteKey(target);
      if (!options.has(key)) {
        options.set(key, {
          key,
          label: target.siteName || 'Site',
        });
      }
    });
    return Array.from(options.values());
  }, [targetSiteKey, targets]);

  const allAreaOptions = useMemo(() => {
    const options = new Map();
    targets.forEach((target) => {
      const key = targetAreaKey(target);
      if (!options.has(key)) {
        options.set(key, {
          key,
          siteKey: targetSiteKey(target),
          label: target.areaName || 'Area',
          siteLabel: target.siteName || 'Site',
        });
      }
    });
    return Array.from(options.values());
  }, [targetAreaKey, targetSiteKey, targets]);

  const areaOptions = useMemo(
    () => allAreaOptions.filter((area) => !browseSiteKey || area.siteKey === browseSiteKey),
    [allAreaOptions, browseSiteKey]
  );

  const visibleTargets = useMemo(() => {
    const activeAreaKey = browseAreaKey || targetAreaKey(activeTarget);
    return targets.filter((target) => targetAreaKey(target) === activeAreaKey);
  }, [activeTarget, browseAreaKey, targetAreaKey, targets]);

  React.useEffect(() => {
    if (!targetMenuOpen) return undefined;
    const areaTarget = visibleTargets[0];
    setTargetMenuScreens([]);
    setTargetMenuStates({});
    if (!areaTarget?.areaId) return undefined;
    let cancelled = false;
    let pending = false;
    const refresh = async () => {
      if (pending || document.visibilityState === 'hidden') return;
      pending = true;
      try {
        // The cached summary supplies source and power evidence without device commands.
        const summary = await getRemoteVenueSummary(areaTarget.areaId);
        if (!cancelled) {
          setTargetMenuScreens(Array.isArray(summary?.screens) ? summary.screens : []);
          setTargetMenuStates(summary?.playbackStates || {});
          setTargetMenuNow(Date.now());
          const refreshed = await refreshVenueInputReports(summary);
          if (!cancelled) {
            setTargetMenuStates(refreshed.playbackStates);
            setTargetMenuNow(Date.now());
          }
        }
      } catch (_error) { /* Existing evidence ages into unknown; never invent an active connection. */ }
      finally { pending = false; }
    };
    refresh();
    const refreshTimer = window.setInterval(refresh, 15000);
    const clockTimer = window.setInterval(() => setTargetMenuNow(Date.now()), 1000);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      cancelled = true;
      window.clearInterval(refreshTimer);
      window.clearInterval(clockTimer);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [browseAreaKey, targetMenuOpen, visibleTargets]);

  const targetTree = useMemo(() => buildTargetTree({
    screens: targetMenuScreens, targets: visibleTargets,
    liveStates: targetMenuStates, now: targetMenuNow,
  }), [targetMenuStates, targetMenuScreens, targetMenuNow, visibleTargets]);

  const handleTargetSelection = async (targetId, screenId = null) => {
    if (powerRoutineBusy || launcherInFlight.current) return;
    setScreenContextId(screenId);
    await selectTarget(targetId);
    setTargetMenuOpen(false);
  };

  const navigateTo = useCallback((page, { behavior = 'auto' } = {}) => {
    const refs = {
      venue: venueScreenRef,
      remote: controlsScreenRef,
      schedule: suitestreamScreenRef,
      launcher: launcherScreenRef,
      epg: epgScreenRef,
    };
    const target = refs[page]?.current;
    if (!target) return;
    const portraitPage = getRemotePortraitPage(page);
    activePortraitPageRef.current = portraitPage;
    setActivePortraitPage(portraitPage);
    const screens = screensRef.current;
    screens?.scrollTo({
      left: getRemotePageIndex(portraitPage) * screens.clientWidth,
      behavior,
    });
  }, []);
  React.useEffect(() => {
    if (!assignedOperation?.id || !['pending', 'running'].includes(assignedOperation.status) || launcherBusyKey) return undefined;
    let active = true;
    const timer = setInterval(async () => {
      try { const result = await getRemoteAssignedOperation(assignedOperation.deviceId, assignedOperation.id); if (active) setAssignedOperation({ ...result, deviceId: assignedOperation.deviceId }); }
      catch (failure) { if (active) setLauncherMessage(failure.message); }
    }, 1000);
    return () => { active = false; clearInterval(timer); };
  }, [assignedOperation?.id, assignedOperation?.status, assignedOperation?.deviceId, launcherBusyKey]);
  React.useEffect(() => { setEpgNavigation(null); }, [selectedTargetId]);
  React.useEffect(() => {
    let active = true;
    setAssignedOperation(null);
    let saved; try { saved = JSON.parse(sessionStorage.getItem(`phone-app-operation:${selectedTargetId}`)); } catch (_) { /* Ignore malformed local state. */ }
    if (saved?.id && saved.deviceId) getRemoteAssignedOperation(saved.deviceId, saved.id).then((result) => { if (active) setAssignedOperation({ ...result, deviceId: saved.deviceId }); }).catch(() => {});
    return () => { active = false; };
  }, [selectedTargetId]);

  const acceptEpgNavigation = useCallback((navigation) => {
    setEpgNavigation((current) => current === navigation ? null : current);
  }, []);

  const handleVenueRemote = useCallback(async (targetDeviceId, screenId = null) => {
    if (powerRoutineBusy || launcherInFlight.current) return;
    setScreenContextId(screenId);
    if (targetDeviceId && String(targetDeviceId) !== String(selectedTargetId)) {
      await selectTarget(targetDeviceId);
    }
    navigateTo('remote');
  }, [navigateTo, selectTarget, selectedTargetId, powerRoutineBusy]);

  const handleVenueAction = useCallback(async (action) => {
    if (action?.type === 'open_remote') {
      await handleVenueRemote(action.targetDeviceId);
      return;
    }
    navigateTo('remote');
  }, [handleVenueRemote, navigateTo]);

  const stopScanner = useCallback(async ({ cancelSession = true } = {}) => {
    scannerControlsRef.current?.stop?.();
    scannerControlsRef.current = null;
    const sessionId = scannerSessionRef.current;
    scannerSessionRef.current = '';
    scannerCompletingRef.current = false;
    scannerSnapshottingRef.current = false;
    setScannerOpen(false);
    setScannerStatus('idle');
    setScannerZoom(null);
    setScannerZoomRange(null);
    if (cancelSession && sessionId) {
      await cancelRemoteIdentificationScan(sessionId).catch(() => {});
    }
  }, []);

  const handleScannedQr = useCallback(async (rawValue) => {
    if (scannerCompletingRef.current) return;
    const match = String(rawValue || '').match(/^SS1:([A-Za-z0-9_-]{22})$/);
    if (!match || !scannerSessionRef.current) {
      setScannerMessage('That is not a device code from this scan.');
      return;
    }
    scannerCompletingRef.current = true;
    setScannerStatus('connecting');
    setScannerMessage('Connecting...');
    try {
      const result = await completeRemoteIdentificationScan({
        sessionId: scannerSessionRef.current,
        token: match[1],
      });
      scannerSessionRef.current = '';
      scannerControlsRef.current?.stop?.();
      scannerControlsRef.current = null;
      setScreenContextId(null);
      await selectTarget(result.targetDeviceId);
      setTargetMenuOpen(false);
      setScannerOpen(false);
      setScannerStatus('idle');
      setScannerZoom(null);
      setScannerZoomRange(null);
    } catch (error) {
      scannerCompletingRef.current = false;
      setScannerStatus('scanning');
      setScannerMessage(error.message || 'Could not select this device.');
    }
  }, [selectTarget]);

  const handleScannerSnapshot = useCallback(async () => {
    if (scannerSnapshottingRef.current || scannerCompletingRef.current) return;
    const video = scannerVideoRef.current;
    if (!video?.videoWidth || !video?.videoHeight) {
      setScannerMessage('The camera is not ready yet. Try again in a moment.');
      return;
    }

    scannerSnapshottingRef.current = true;
    const sessionId = scannerSessionRef.current;
    setScannerStatus('snapshotting');
    setScannerMessage('Reading snapshot...');
    try {
      await new Promise((resolve) => window.setTimeout(resolve, 50));
      if (!sessionId || scannerSessionRef.current !== sessionId) return;

      const fullFrame = document.createElement('canvas');
      fullFrame.width = video.videoWidth;
      fullFrame.height = video.videoHeight;
      const fullContext = fullFrame.getContext('2d');
      if (!fullContext) throw new Error('Could not capture the camera image.');
      fullContext.drawImage(video, 0, 0, fullFrame.width, fullFrame.height);

      const cropSize = Math.round(Math.min(fullFrame.width, fullFrame.height) * 0.8);
      const centerCrop = document.createElement('canvas');
      centerCrop.width = cropSize;
      centerCrop.height = cropSize;
      const cropContext = centerCrop.getContext('2d');
      if (!cropContext) throw new Error('Could not capture the camera image.');
      cropContext.drawImage(
        fullFrame,
        Math.round((fullFrame.width - cropSize) / 2),
        Math.round((fullFrame.height - cropSize) / 2),
        cropSize,
        cropSize,
        0,
        0,
        cropSize,
        cropSize
      );

      const [{ BrowserQRCodeReader }, { BarcodeFormat, DecodeHintType }] = await Promise.all([
        import('@zxing/browser'),
        import('@zxing/library'),
      ]);
      const hints = new Map([
        [DecodeHintType.POSSIBLE_FORMATS, [BarcodeFormat.QR_CODE]],
        [DecodeHintType.TRY_HARDER, true],
      ]);
      let result;
      try {
        result = new BrowserQRCodeReader(hints).decodeFromCanvas(centerCrop);
      } catch {
        result = new BrowserQRCodeReader(hints).decodeFromCanvas(fullFrame);
      }
      if (!sessionId || scannerSessionRef.current !== sessionId) return;
      await handleScannedQr(result.getText());
      if (!scannerCompletingRef.current) setScannerStatus('scanning');
    } catch (error) {
      if (!scannerCompletingRef.current && scannerSessionRef.current === sessionId) {
        setScannerStatus('scanning');
        setScannerMessage(
          error?.message === 'Could not capture the camera image.'
            ? error.message
            : 'No device QR code found in the snapshot. Hold steady, fill the frame, and try again.'
        );
      }
    } finally {
      scannerSnapshottingRef.current = false;
    }
  }, [handleScannedQr]);

  const startScanner = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setScannerOpen(true);
      setScannerStatus('error');
      setScannerMessage('Camera scanning is not supported by this browser.');
      return;
    }
    const activeAreaId = activeTarget?.areaId || device?.areaId;
    setScannerOpen(true);
    setScannerStatus('starting');
    setScannerMessage('Starting camera and identifying screens...');
    setTargetMenuOpen(false);

    let startupCancelled = false;
    try {
      await new Promise((resolve) => window.requestAnimationFrame(resolve));
      const [{ BrowserQRCodeReader }, { BarcodeFormat, DecodeHintType }] = await Promise.all([
        import('@zxing/browser'),
        import('@zxing/library'),
      ]);
      const hints = new Map([
        [DecodeHintType.POSSIBLE_FORMATS, [BarcodeFormat.QR_CODE]],
        [DecodeHintType.TRY_HARDER, true],
      ]);
      const reader = new BrowserQRCodeReader(hints, {
        delayBetweenScanAttempts: 100,
        delayBetweenScanSuccess: 300,
      });
      const sessionPromise = startRemoteIdentificationScan(activeAreaId);
      const cameraPromise = reader.decodeFromConstraints(
        {
          audio: false,
          video: {
            facingMode: { ideal: 'environment' },
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          },
        },
        scannerVideoRef.current,
        (result) => {
          if (result) handleScannedQr(result.getText());
        }
      );
      sessionPromise.then((session) => {
        if (startupCancelled) {
          cancelRemoteIdentificationScan(session.sessionId).catch(() => {});
          return;
        }
        scannerSessionRef.current = session.sessionId;
      }).catch(() => {});
      cameraPromise.then((controls) => {
        if (startupCancelled) {
          controls.stop();
          return;
        }
        scannerControlsRef.current = controls;
      }).catch(() => {});
      const [session, controls] = await Promise.all([sessionPromise, cameraPromise]);
      setScannerStatus('scanning');
      setScannerMessage(
        `${session.screenCount} screen${session.screenCount === 1 ? '' : 's'} identified. Keep the whole code inside the frame.`
      );

      const capabilities = controls.streamVideoCapabilitiesGet?.(
        (track) => track.kind === 'video'
      );
      if (Array.isArray(capabilities?.focusMode) && capabilities.focusMode.includes('continuous')) {
        controls.streamVideoConstraintsApply?.({
          advanced: [{ focusMode: 'continuous' }],
        }).catch(() => {});
      }
      const zoom = capabilities?.zoom;
      if (zoom && Number.isFinite(zoom.min) && Number.isFinite(zoom.max) && zoom.max > zoom.min) {
        const initial = Number(zoom.min);
        setScannerZoom(initial);
        setScannerZoomRange({
          min: initial,
          max: Number(zoom.max),
          step: Number(zoom.step) || 0.1,
        });
      }
      window.setTimeout(() => {
        if (scannerSessionRef.current === session.sessionId) {
          setScannerMessage('Scan timed out.');
          stopScanner();
        }
      }, 45000);
    } catch (error) {
      startupCancelled = true;
      scannerControlsRef.current?.stop?.();
      await stopScanner();
      setScannerOpen(true);
      setScannerStatus('error');
      setScannerMessage(error.message || 'Could not start the QR scanner.');
    }
  }, [activeTarget?.areaId, device?.areaId, handleScannedQr, stopScanner]);

  const handleScannerZoom = async (value) => {
    const nextZoom = Number(value);
    setScannerZoom(nextZoom);
    try {
      await scannerControlsRef.current?.streamVideoConstraintsApply?.({
        advanced: [{ zoom: nextZoom }],
      });
    } catch {
      setScannerMessage('Camera zoom is not available on this phone.');
    }
  };

  React.useEffect(() => () => {
    scannerControlsRef.current?.stop?.();
    const sessionId = scannerSessionRef.current;
    if (sessionId) cancelRemoteIdentificationScan(sessionId).catch(() => {});
  }, []);

  const handlePreviewToggle = () => {
    const nextOpen = !previewOpen;
    setPreviewOpen(nextOpen);
    if (nextOpen) {
      window.requestAnimationFrame(() => {
        screensRef.current?.scrollTo({
          left: controlsScreenRef.current?.offsetLeft || 0,
          behavior: 'smooth',
        });
      });
    }
  };

  const landscapeCommandAvailable = (command) => {
    if (powerRoutineBusy || launcherInFlight.current || (command === 'power' && (routinesEnabled || screenModePending))) return false;
    if (!remoteAvailableCommands) return true;
    if (remoteAvailableCommands instanceof Set) return remoteAvailableCommands.has(command);
    if (Array.isArray(remoteAvailableCommands)) return remoteAvailableCommands.includes(command);
    if (typeof remoteAvailableCommands === 'object') return Boolean(remoteAvailableCommands[command]);
    return true;
  };

  const renderLandscapeControl = (command, label, icon = null, className = '') => (
    <button
      type="button"
      className={`phone-remote__landscape-control ${className}`}
      aria-label={label}
      title={label}
      disabled={!landscapeCommandAvailable(command)}
      onPointerDown={(event) => {
        event.preventDefault();
        if (landscapeCommandAvailable(command)) handleCommand(command);
      }}
      onClick={(event) => event.preventDefault()}
    >
      {icon}
      {!icon ? <span>{label}</span> : null}
    </button>
  );

  React.useEffect(() => {
    if (!activeTarget) return;
    const nextSiteKey = targetSiteKey(activeTarget);
    const nextAreaKey = targetAreaKey(activeTarget);
    setBrowseSiteKey(nextSiteKey);
    setBrowseAreaKey(nextAreaKey);
  }, [activeTarget, targetAreaKey, targetSiteKey]);

  React.useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const media = typeof window.matchMedia === 'function'
      ? window.matchMedia('(orientation: landscape)')
      : null;
    let alignFrame = 0;
    let settleTimer = 0;
    let releaseTimer = 0;
    let inputFrame = 0;
    const alignToPage = (page) => {
      const align = () => {
        const screens = screensRef.current;
        if (!screens) return;
        screens.scrollTo({
          left: getRemotePageIndex(page) * screens.clientWidth,
          behavior: 'auto',
        });
      };
      window.cancelAnimationFrame(alignFrame);
      window.clearTimeout(settleTimer);
      window.clearTimeout(releaseTimer);
      navigationAlignmentRef.current = true;
      alignFrame = window.requestAnimationFrame(() => {
        alignFrame = window.requestAnimationFrame(align);
      });
      // Mobile Safari reports rotation dimensions in stages; snap once more after they settle.
      settleTimer = window.setTimeout(align, 180);
      releaseTimer = window.setTimeout(() => {
        navigationAlignmentRef.current = false;
      }, 260);
    };
    const update = () => {
      const viewport = window.visualViewport;
      const width = Math.round(window.innerWidth);
      const height = viewport?.height || window.innerHeight;
      setViewportHeight(Math.round(height));
      const widthChanged = Math.abs(width - viewportWidthRef.current) > 1;
      viewportWidthRef.current = width;
      const focusedInput = document.activeElement;
      const editing = focusedInput?.matches('input, textarea, [contenteditable="true"]') === true;
      window.cancelAnimationFrame(inputFrame);
      if (editing) {
        // Wait for the viewport height to reach the scroll panel before positioning the field.
        inputFrame = window.requestAnimationFrame(() => {
          inputFrame = window.requestAnimationFrame(() => {
            if (document.activeElement === focusedInput) keepRemoteInputVisible(focusedInput, window.visualViewport);
          });
        });
      }
      // Desktop monitors keep their physical orientation even in a narrow browser window.
      const handheldOrientation = window.matchMedia?.('(pointer: coarse)')?.matches
        ? window.screen?.orientation?.type : undefined;
      const nextLandscape = isRemoteLandscape({
        deviceOrientation: handheldOrientation ?? window.orientation,
        widthChanged,
        previousLandscape: isLandscapeRef.current,
        orientationMatches: media ? media.matches : undefined,
        width: window.innerWidth,
        height: window.innerHeight,
      });
      if (nextLandscape === isLandscapeRef.current) {
        if (widthChanged) alignToPage(activePortraitPageRef.current);
        return;
      }
      isLandscapeRef.current = nextLandscape;
      setIsLandscape(nextLandscape);
      alignToPage(activePortraitPageRef.current);
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);
    document.addEventListener('focusin', update);
    document.addEventListener('focusout', update);
    window.screen?.orientation?.addEventListener?.('change', update);
    window.visualViewport?.addEventListener('scroll', update);
    window.visualViewport?.addEventListener('resize', update);
    if (typeof media?.addEventListener === 'function') {
      media.addEventListener('change', update);
    } else {
      media?.addListener?.(update);
    }
    return () => {
      window.cancelAnimationFrame(alignFrame);
      window.cancelAnimationFrame(inputFrame);
      window.clearTimeout(settleTimer);
      window.clearTimeout(releaseTimer);
      navigationAlignmentRef.current = false;
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
      document.removeEventListener('focusin', update);
      document.removeEventListener('focusout', update);
      window.screen?.orientation?.removeEventListener?.('change', update);
      window.visualViewport?.removeEventListener('scroll', update);
      window.visualViewport?.removeEventListener('resize', update);
      if (typeof media?.removeEventListener === 'function') {
        media.removeEventListener('change', update);
      } else {
        media?.removeListener?.(update);
      }
    };
  }, []);

  React.useEffect(() => {
    document.documentElement.classList.add('phone-remote-document');
    document.body.classList.add('phone-remote-document');
    return () => {
      document.documentElement.classList.remove('phone-remote-document');
      document.body.classList.remove('phone-remote-document');
    };
  }, []);

  React.useEffect(() => {
    const screens = screensRef.current;
    if (!screens) return undefined;

    const update = () => {
      if (navigationAlignmentRef.current) return;
      const candidates = [
        { page: 'venue', ref: isLandscapeRef.current ? suitestreamScreenRef : venueScreenRef },
        { page: 'remote', ref: controlsScreenRef },
        { page: 'launcher', ref: isLandscapeRef.current ? epgScreenRef : launcherScreenRef },
      ].filter((entry) => entry.ref.current);
      const nearest = candidates.sort((a, b) =>
        Math.abs(screens.scrollLeft - getRemotePageIndex(a.page) * screens.clientWidth)
        - Math.abs(screens.scrollLeft - getRemotePageIndex(b.page) * screens.clientWidth)
      )[0];
      if (!nearest || Math.abs(screens.scrollLeft - getRemotePageIndex(nearest.page) * screens.clientWidth) > 2) return;
      setActivePortraitPage(nearest.page);
      activePortraitPageRef.current = nearest.page;
    };

    update();
    screens.addEventListener('scroll', update, { passive: true });
    return () => {
      screens.removeEventListener('scroll', update);
    };
  }, [bootStatus, liveExperienceInstances]);

  const selectedAreaId = Number(activeTarget?.areaId || remoteDevice?.areaId || 0) || null;

  const loadLiveExperienceInstances = useCallback(async ({ silent = false } = {}) => {
    if (!remoteDevice?.id || !selectedAreaId) {
      setLiveExperienceInstances([]);
      setLiveExperienceStatus('ready');
      return;
    }
    try {
      if (!silent) setLiveExperienceStatus('loading');
      const result = await listRemoteLiveExperienceInstances({ areaId: selectedAreaId });
      setLiveExperienceInstances(Array.isArray(result?.instances) ? result.instances : []);
      setLiveExperienceMessage('');
      setLiveExperienceStatus('ready');
    } catch (error) {
      setLiveExperienceMessage(error.message || 'Live experiences could not be loaded.');
      setLiveExperienceStatus('error');
    }
  }, [remoteDevice?.id, selectedAreaId]);

  React.useEffect(() => {
    loadLiveExperienceInstances();
    const timer = window.setInterval(
      () => loadLiveExperienceInstances({ silent: true }),
      10000
    );
    return () => window.clearInterval(timer);
  }, [loadLiveExperienceInstances]);

  const selectedActiveExperience = useMemo(
    () => getSelectedActiveLiveExperience(liveExperienceInstances, selectedTargetId),
    [liveExperienceInstances, selectedTargetId]
  );

  const handleLiveExperienceLifecycle = useCallback(async (instance, action) => {
    if (!instance?.id || !selectedAreaId || launcherInFlight.current || powerRoutineBusy || screenModePending) return;
    const entry = launcherDevices.find((device) => Number(device.appControlDeviceId || device.target.id) === Number(instance.primaryPlaybackDeviceId));
    if (action === 'start' && (!entry?.switchReady || launcherStatus !== 'ready')) return;
    launcherInFlight.current = true;
    const lifecycle = instance.pluginKey === 'jukebox'
      ? action === 'stop' ? stopRemoteJukeboxRuntime : launchRemoteJukeboxRuntime
      : action === 'stop' ? stopRemoteKaraokeRuntime : launchRemoteKaraokeRuntime;
    try {
      setLiveExperienceBusyId(instance.id);
      setLiveExperienceMessage('');
      setLauncherBusyKey(`experience:${instance.pluginKey}:${instance.id}`);
      const selection = selectionRef.current;
      if (action === 'start') {
        if (!await confirmLauncherDevice(entry, selection)) return;
        await clearRemoteLauncherAds({ targetDeviceId: entry.target.id, logicalScreenId: entry.screen?.id });
      }
      if (selectionRef.current !== selection) throw new Error('Selection changed; experience was not started.');
      await lifecycle({ areaId: selectedAreaId, eventInstanceId: instance.id });
      if (action === 'start' && entry.switchRequired) await selectTarget(entry.target.id);
      await loadLiveExperienceInstances({ silent: true });
    } catch (error) {
      setLiveExperienceMessage(error.message || `Failed to ${action} ${instance.name}.`);
    } finally {
      setLiveExperienceBusyId(null);
      setLauncherBusyKey('');
      launcherInFlight.current = false;
    }
  }, [loadLiveExperienceInstances, selectedAreaId, launcherDevices, launcherStatus, powerRoutineBusy, screenModePending, confirmLauncherDevice, selectTarget]);

  const handleOpenLiveExperienceControls = useCallback(async (instance) => {
    if (powerRoutineBusy || launcherInFlight.current) return;
    setScreenContextId(null);
    if (instance?.primaryPlaybackDeviceId) {
      await selectTarget(instance.primaryPlaybackDeviceId);
    }
    navigateTo('remote');
  }, [navigateTo, selectTarget, powerRoutineBusy]);

  const renderMediaLinkPanel = ({ areaId, screens }) => (
    <RemoteMediaLinkPanel
      busyChoice={mediaLinkBusyChoice}
      input={mediaLinkInput}
      selectedTargetName={activeTarget?.name}
      playbackOptions={getMediaPlaybackOptions({
        choices: mediaLinkPreview?.choices,
        devices: launcherDevices.filter((device) => !device.screen || screens.some((screen) => Number(screen.id) === Number(device.screen.id))),
        selectedTargetId,
      })}
      playbackBlocked={powerRoutineBusy || screenModePending || Boolean(launcherBusyKey) || launcherStatus !== 'ready'}
      message={mediaLinkMessage}
      onAction={handleMediaLinkAction}
      onChange={(value) => {
        setMediaLinkInput(value);
        setMediaLinkPreview(null);
        setMediaLinkStatus('idle');
        setMediaLinkMessage('');
      }}
      onPaste={handlePasteMediaLink}
      onPlaySuitestream={(device) => handleMediaSuitestreamPlay({ areaId, device })}
      onPreview={handleMediaLinkPreview}
      onSavePlaylist={() => handleSaveAreaPlaylist({ areaId })}
      onSelectResult={handleSelectMediaSearchResult}
      preview={mediaLinkPreview}
      status={mediaLinkStatus}
    />
  );

  const navigationItems = getRemoteNavigationItems(isLandscape);

  if (bootStatus === 'loading') {
    return (
      <RemotePwaSupport surface="remote">
        <div className="phone-remote">
          <div className="phone-remote__shell phone-remote__shell--centered">
            <section className="phone-remote__panel phone-remote__panel--hero">
              <div className="phone-remote__eyebrow">Phone Remote</div>
              <h1 className="phone-remote__title">Connecting to your remote</h1>
              <p className="phone-remote__copy">{bootMessage}</p>
            </section>
          </div>
        </div>
      </RemotePwaSupport>
    );
  }

  return (
    <RemotePwaSupport surface="remote" promptTargetId="phone-remote-install-prompt-slot">
      <div
        className={`phone-remote${isLandscape ? ' phone-remote--landscape' : ''}${isLandscape && activeNavigation === 'epg' ? ' phone-remote--epg' : ''}`}
        style={viewportHeight ? { '--phone-remote-viewport-height': `${viewportHeight}px` } : undefined}
      >
        <div className="phone-remote__topbar">
          <button
            type="button"
            className={`phone-remote__topbar-title${targetMenuOpen ? ' phone-remote__topbar-title--active' : ''}`}
            aria-expanded={targetMenuOpen}
            onClick={() => setTargetMenuOpen((current) => !current)}
            disabled={!targets.length || targetLoading || powerRoutineBusy || Boolean(launcherBusyKey)}
          >
            <span className="phone-remote__topbar-target-copy">
              <strong>{activeTarget?.name || remoteDevice?.name || 'Remote'}</strong>
            </span>
            <span className="phone-remote__topbar-target-cue">
              <ChevronDown className="h-4 w-4" />
            </span>
          </button>
          <div className="phone-remote__topbar-actions">
            <button
              type="button"
              className={`phone-remote__topbar-btn${previewOpen ? ' phone-remote__topbar-btn--active' : ''}`}
              onClick={handlePreviewToggle}
            >
              Preview
            </button>
            {(routinesEnabled ? routineScreen && screenWatchSource : watchTarget) ? (
              <button
                type="button"
                className="phone-remote__topbar-btn"
                onClick={routinesEnabled ? () => screenExecution.run('activate_source', Number(screenWatchSource.deviceId || screenWatchSource.id)) : handleWatchSource}
                disabled={screenModePending || powerRoutineBusy || Boolean(launcherBusyKey)}
                title={routinesEnabled ? `Show the selected source on ${routineScreen.name}` : `Show ${watchTarget?.playerName || 'this player'} on ${watchTarget?.televisionName || 'the connected TV'}`}
              >
                Watch
              </button>
            ) : null}
          </div>
          {targetMenuOpen ? (
            <div className="phone-remote__target-menu">
              <div className="phone-remote__site-area-menu">
                <div className="phone-remote__menu-field">
                  <span>Site</span>
                  <select
                    aria-label="Site"
                    className="phone-remote__select phone-remote__select--compact"
                    value={browseSiteKey}
                    disabled={siteOptions.length < 2}
                    onChange={(event) => {
                      const nextSiteKey = event.target.value;
                      const nextArea = allAreaOptions.find((area) => area.siteKey === nextSiteKey);
                      setBrowseSiteKey(nextSiteKey);
                      setBrowseAreaKey(nextArea?.key || '');
                    }}
                  >
                    {siteOptions.map((site) => <option key={site.key} value={site.key}>{site.label}</option>)}
                  </select>
                </div>

                <div className="phone-remote__menu-field">
                  <span>Area</span>
                  <select
                    aria-label="Area"
                    className="phone-remote__select phone-remote__select--compact"
                    value={browseAreaKey}
                    disabled={areaOptions.length < 2}
                    onChange={(event) => setBrowseAreaKey(event.target.value)}
                  >
                    {areaOptions.map((area) => <option key={area.key} value={area.key}>{area.label}</option>)}
                  </select>
                </div>
                <button type="button" className="phone-remote__mini-btn" onClick={startScanner}>Scan QR</button>
              </div>

              <div className="phone-remote__target-list phone-remote__target-tree">
                {targetTree.branches.map((branch) => (
                  <div
                    className={`phone-remote__target-branch${branch.activeDeviceId && branch.activeDeviceId !== Number(branch.parent?.id) ? ' phone-remote__target-branch--playing-child' : ''}`}
                    key={`screen-${branch.id}`}
                  >
                    <div className={`phone-remote__target-parent${branch.activeDeviceId === Number(branch.parent?.id) ? ' phone-remote__target-parent--playing' : ''}`}>
                      {branch.parent ? (
                        <button
                          type="button"
                          className={`phone-remote__target-option phone-remote__target-option--parent${String(branch.parent.id) === String(selectedTargetId) ? ' phone-remote__target-option--active' : ''}${branch.activeDeviceId === Number(branch.parent.id) ? ' phone-remote__target-option--playing' : ''}`}
                          onClick={() => handleTargetSelection(branch.parent.id, branch.id)}
                        >
                          <span className="phone-remote__target-icon">TV</span>
                          <span className="phone-remote__target-option-copy">
                            <strong>{branch.parent.name || branch.name}</strong>
                          </span>
                          {branch.activeDeviceId === Number(branch.parent.id) ? <em>On screen</em> : null}
                        </button>
                      ) : (
                        <div className="phone-remote__target-screen-label">{branch.name}</div>
                      )}
                    </div>
                    {branch.children.length ? (
                      <div className="phone-remote__target-children">
                        {branch.children.map(({ target, expectedInput }, index) => {
                          const isPlaying = branch.activeDeviceId === Number(target.id);
                          return (
                            <div className={`phone-remote__target-child${isPlaying ? ' phone-remote__target-child--playing' : ''}`} key={target.id}>
                              <span className="phone-remote__target-tree-joint">{index === branch.children.length - 1 ? '└' : '├'}</span>
                              <button
                                type="button"
                                className={`phone-remote__target-option${String(target.id) === String(selectedTargetId) && (!routinesEnabled || Number(routineScreen?.id) === Number(branch.id)) ? ' phone-remote__target-option--active' : ''}${isPlaying ? ' phone-remote__target-option--playing' : ''}`}
                                onClick={() => handleTargetSelection(target.id, branch.id)}
                              >
                                <span className="phone-remote__target-icon">STB</span>
                                <span className="phone-remote__target-option-copy">
                                  <strong>{target.name || 'Unnamed player'}</strong>
                                  <span>{expectedInput || 'Input not mapped'}</span>
                                </span>
                                {isPlaying ? <em>Playing now</em> : null}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    ) : null}
                  </div>
                ))}
                {targetTree.ungrouped.length ? (
                  <div className="phone-remote__target-ungrouped">
                    <span>Other devices</span>
                    {targetTree.ungrouped.map((target) => (
                      <button
                        key={target.id}
                        type="button"
                        className={`phone-remote__target-option${String(target.id) === String(selectedTargetId) ? ' phone-remote__target-option--active' : ''}`}
                        onClick={() => handleTargetSelection(target.id)}
                      >
                        <span className="phone-remote__target-option-copy">
                          <strong>{target.name || 'Unnamed device'}</strong>
                          <span>{formatTargetOption(target)}</span>
                        </span>
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>

        <nav className="phone-remote__nav" aria-label="Phone remote sections">
          {navigationItems.map(([page, label]) => (
            <button
              key={page}
              type="button"
              className={activeNavigation === page ? 'phone-remote__nav-item phone-remote__nav-item--active' : 'phone-remote__nav-item'}
              aria-current={activeNavigation === page ? 'page' : undefined}
              onClick={() => navigateTo(page)}
            >
              {label}
            </button>
          ))}
        </nav>

        {scannerOpen ? (
          <div className="phone-remote__scanner" role="dialog" aria-modal="true" aria-label="Scan device QR code">
            <video ref={scannerVideoRef} autoPlay muted playsInline />
            <div className="phone-remote__scanner-shade" />
            <div className="phone-remote__scanner-frame" />
            <div className="phone-remote__scanner-bar">
              <div>
                <strong>Scan a TV</strong>
                <span>{scannerMessage}</span>
              </div>
              <button type="button" onClick={() => stopScanner()} aria-label="Close scanner">Close</button>
            </div>
            {scannerZoomRange ? (
              <label className="phone-remote__scanner-zoom">
                <span>Zoom</span>
                <input
                  type="range"
                  min={scannerZoomRange.min}
                  max={scannerZoomRange.max}
                  step={scannerZoomRange.step}
                  value={scannerZoom}
                  aria-label="Camera zoom"
                  aria-orientation="vertical"
                  onPointerDown={(event) => event.stopPropagation()}
                  onPointerMove={(event) => event.stopPropagation()}
                  onChange={(event) => handleScannerZoom(event.target.value)}
                />
              </label>
            ) : null}
            {scannerStatus !== 'error' ? (
              <div className="phone-remote__scanner-actions" aria-live="polite">
                <button
                  type="button"
                  className="phone-remote__scanner-connect"
                  disabled={scannerStatus !== 'scanning'}
                  onPointerDown={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    handleScannerSnapshot();
                  }}
                  onClick={(event) => event.preventDefault()}
                >
                  {scannerStatus === 'snapshotting'
                    ? 'Reading...'
                    : scannerStatus === 'connecting' ? 'Connecting...' : 'Connect'}
                </button>
                <span>{scannerMessage || 'Hold the QR code inside the frame, then tap Connect.'}</span>
              </div>
            ) : null}
            {scannerStatus === 'error' ? (
              <div className="phone-remote__scanner-error">{scannerMessage}</div>
            ) : null}
          </div>
        ) : null}

        <div className="phone-remote__screens" ref={screensRef}>
          <section className="phone-remote__screen phone-remote__screen--venue" aria-label="Venue" ref={venueScreenRef}>
            <div className="phone-remote__screen-inner">
              <VenueDashboard
                areaId={activeTarget?.areaId || remoteDevice?.areaId}
                selectedTargetId={selectedTargetId}
                selectedScreenId={screenContextId}
                controlsBlocked={targetLoading || powerRoutineBusy || Boolean(launcherBusyKey)}
                addSomething={renderMediaLinkPanel}
                previewsPaused={previewOpen}
                enabled={Boolean(remoteDevice?.id) && activeNavigation === 'venue' && !isLandscape}
                onAction={handleVenueAction}
                onOpenRemote={handleVenueRemote}
                onOpenSchedule={() => navigateTo('schedule')}
              />
            </div>
          </section>

          <section className="phone-remote__screen phone-remote__screen--epg" aria-label="EPG" ref={epgScreenRef}>
            <RemoteEpg key={selectedTargetId} targetId={selectedTargetId}
              enabled={isLandscape && activeNavigation === 'epg'}
              navigation={epgNavigation} onNavigationAccepted={acceptEpgNavigation}
              blocked={targetLoading || screenExecution.busy || siteExecution.busy || Boolean(launcherBusyKey)}
              rotations={launcherAds.adPlaylists || []} adDevice={epgAdDevice(launcherDevices, selectedTargetId)}
              adStatus={launcherAdsStatus} adMessage={launcherMessage}
              onRefresh={() => setLauncherRefresh((value) => value + 1)}
              onBusyChange={setEpgBusy}
              onPlayAd={(launcherDevice, rotation) => launchFromSharedScreen(launcherDevice, {
                kind: 'ad_playlist', id: rotation.id, label: rotation.name, slot: 'full', pauseBackground: true,
              })} />
          </section>

          <section
            className="phone-remote__screen phone-remote__screen--launcher"
            aria-label="Launcher"
            ref={launcherScreenRef}
          >
            <div className="phone-remote__screen-inner">
              <div className="phone-remote__launcher-portrait">
                <div className="phone-remote__more-heading">
                  <div className="phone-remote__page-title">Launcher</div>
                </div>
                <RemoteEpgSearch key={`launcher-search:${selectedTargetId}`} targetId={selectedTargetId} variant="launcher"
                  enabled={Boolean(selectedTargetId) && !targetLoading && !isLandscape && activeNavigation === 'launcher'}
                  onNavigate={(result) => { setEpgNavigation({ targetId: selectedTargetId, result }); navigateTo('launcher'); }} />
                {epgNavigation && epgNavigation.targetId === selectedTargetId && <div className="phone-remote__epg-selection" role="status">
                  <strong>{epgNavigation.result.programme.title}</strong>
                  <p>Turn your phone sideways to open this show in the EPG.</p>
                  <button type="button" className="phone-remote__mini-btn" onClick={() => setEpgNavigation(null)}>Cancel selection</button>
                </div>}
                <button type="button" className="phone-remote__action-btn"
                  disabled={launcherStatus === 'loading' || Boolean(launcherBusyKey) || powerRoutineBusy}
                  onClick={() => setLauncherRefresh((value) => value + 1)}>Refresh launcher</button>
                {launcherStatus !== 'error' ? (
                  <>
                    <LauncherPanels devices={launcherDevices} playlists={areaLauncherContent.playlists || []}
                      contentTargets={areaLauncherContent.contentTargets || []} status={launcherStatus}
                      busyKey={launcherBusyKey} blocked={Boolean(launcherBusyKey) || powerRoutineBusy || screenModePending || launcherStatus !== 'ready'}
                      onLaunch={launchFromSharedScreen} />
                    <LauncherAdsPanel devices={launcherDevices} adPlaylists={launcherAds.adPlaylists || []}
                      campaigns={launcherAds.campaigns || []} socialPosts={launcherAds.socialPosts || []} status={launcherAdsStatus} message={launcherAdsMessage}
                      busyKey={launcherBusyKey} blocked={Boolean(launcherBusyKey) || powerRoutineBusy || screenModePending || launcherStatus !== 'ready'}
                      onLaunch={launchFromSharedScreen} />
                    <NativeOperationControls key={`${assignedOperation?.deviceId}:${assignedOperation?.id}`} operation={assignedOperation}
                      onContinue={() => continueRemoteAssignedOperation(assignedOperation.deviceId, assignedOperation.id)}
                      onCancel={() => cancelRemoteAssignedOperation(assignedOperation.deviceId, assignedOperation.id)}
                      onResult={(result) => setAssignedOperation({ ...result, deviceId: assignedOperation.deviceId })} onError={(failure) => setLauncherMessage(failure.message)} />
                    {launcherMessage ? <div className="phone-remote__notice">{launcherMessage}</div> : null}
                  </>
                ) : (
                  <div className="phone-remote__notice">
                    {launcherMessage || 'App launcher controls are not available for this TV.'}
                  </div>
                )}

                <LauncherPanel title="Live Experiences" count={`${liveExperienceInstances.length} in this area`}
                  className="phone-remote__launcher-section--queues">
                  {liveExperienceStatus === 'loading' ? (
                    <div className="phone-remote__notice">Loading Karaoke and Jukebox…</div>
                  ) : liveExperienceInstances.length ? (
                    <div className="phone-remote__live-experience-list">
                      {liveExperienceInstances.map((instance) => (
                        <div key={`${instance.pluginKey}-${instance.id}`} className="phone-remote__shortcut-group">
                          <span>
                            {instance.pluginKey === 'jukebox' ? 'Jukebox' : 'Karaoke'}
                            {' • '}
                            {instance.active ? 'Active' : 'Stopped'}
                          </span>
                          <strong>{instance.publicTitle || instance.name}</strong>
                          {instance.publicTitle ? (
                            <span>Powered by {instance.pluginKey === 'jukebox' ? 'Jukebox' : 'Karaoke'}</span>
                          ) : null}
                          <div>
                            {instance.active ? (
                              <button
                                type="button"
                                className="phone-remote__shortcut-btn"
                                onClick={() => handleOpenLiveExperienceControls(instance)}
                              >
                                Controls
                              </button>
                            ) : null}
                            <button
                              type="button"
                              className="phone-remote__shortcut-btn"
                              onClick={() => handleLiveExperienceLifecycle(
                                instance,
                                instance.active ? 'stop' : 'start'
                              )}
                              disabled={Boolean(launcherBusyKey) || powerRoutineBusy || screenModePending || (!instance.active && (launcherStatus !== 'ready' || !launcherDevices.some((entry) => entry.switchReady && Number(entry.appControlDeviceId || entry.target.id) === Number(instance.primaryPlaybackDeviceId))))}
                            >
                              {liveExperienceBusyId === instance.id
                                ? instance.active ? 'Stopping…' : 'Starting…'
                                : instance.active ? 'Stop' : launcherDevices.some((entry) => entry.switchReady && Number(entry.appControlDeviceId || entry.target.id) === Number(instance.primaryPlaybackDeviceId)) ? 'Start' : 'Unavailable on this screen'}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="phone-remote__notice">
                      No Karaoke or Jukebox instances are configured for this area.
                    </div>
                  )}
                  {liveExperienceMessage ? (
                    <div className="phone-remote__notice phone-remote__notice--error">
                      {liveExperienceMessage}
                    </div>
                  ) : null}
                </LauncherPanel>
              </div>

            </div>
          </section>

          <section
            className="phone-remote__screen phone-remote__screen--suitestream"
            aria-label="Suitestream Remote"
            ref={suitestreamScreenRef}
          >
            <div className="phone-remote__screen-inner phone-remote__screen-inner--suitestream">
              <div className="phone-remote__page-title phone-remote__suitestream-title">
                Suitestream Remote
              </div>
              <div className="phone-remote__suitestream-timeline">
                <RemoteScheduleTimeline
                  areaId={activeTarget?.areaId || remoteDevice?.areaId}
                  enabled={activeNavigation === 'schedule'}
                />
              </div>
            </div>
          </section>

          <section
            className="phone-remote__screen phone-remote__screen--controls"
            aria-label={selectedActiveExperience ? 'Live experience controls' : 'D-pad remote'}
            ref={controlsScreenRef}
          >
            <div className="phone-remote__screen-inner phone-remote__screen-inner--controls">
              <div className="phone-remote__page-title phone-remote__page-title--controls">
                {selectedActiveExperience
                  ? `${selectedActiveExperience.pluginKey === 'jukebox' ? 'Jukebox' : 'Karaoke'} Controls`
                  : 'D-pad Remote'}
              </div>
              <div id="phone-remote-install-prompt-slot" className="phone-remote__install-prompt-slot" />
              {selectedActiveExperience ? (
                <>
                  {isLandscape
                    ? <details className="phone-remote__power-options"><summary>Power options</summary>{renderPowerRoutinePanel()}</details>
                    : renderPowerRoutinePanel()}
                  <KaraokeRemotePanel
                    key={`${selectedActiveExperience.pluginKey}-${selectedActiveExperience.id}`}
                    areaId={selectedAreaId}
                    eventInstanceId={selectedActiveExperience.id}
                    onRuntimeChange={() => loadLiveExperienceInstances({ silent: true })}
                    plugin={selectedActiveExperience.pluginKey}
                  />
                </>
              ) : (
                <>
                  {previewOpen ? (
                    <div className="phone-remote__preview-panel">
                      <div className="phone-remote__preview-header">
                        <strong>{activeTarget?.name || device?.name || 'Selected device'}</strong>
                        {canPreviewSnapshot ? (
                          <button
                            type="button"
                            className="phone-remote__primary-btn"
                            onClick={() => fetchLiveSnapshot('manual')}
                            disabled={snapshotStatus === 'loading' || snapshotStatus === 'refreshing'}
                          >
                            Refresh
                          </button>
                        ) : null}
                      </div>
                      {canPreviewSnapshot && previewImageUrl ? (
                        <div className="phone-remote__preview-frame">
                          <img src={previewImageUrl} alt={`Live preview for ${device.name}`} className="phone-remote__preview-image" />
                        </div>
                      ) : (
                        <div className="phone-remote__preview-empty">
                          {supportsPreviewSnapshot
                            ? snapshotMessage || 'Loading preview...'
                            : 'Preview capability was not reported for this device.'}
                        </div>
                      )}
                    </div>
                  ) : null}
                  {device ? (
                    <>
                      <div className="phone-remote__portrait-control">
                        <UnifiedTvControl
                          key={`${device?.id}:${selectedMethod}`}
                          commands={routinesEnabled || screenModePending ? UNIFIED_TV_COMMANDS.filter((entry) => entry.command !== 'power') : UNIFIED_TV_COMMANDS}
                          onCommand={(command, input) => { if (!powerRoutineBusy && !launcherInFlight.current) handlePrimaryRemoteCommand(command, input); }}
                          onInputEnd={handleRemoteInputEnd}
                          available={remoteAvailableCommands}
                          hdmiSources={hdmiSources}
                          tvHomeSource={tvHomeSource}
                          powerControls={renderPowerRoutinePanel()}
                        />
                      </div>
                      <RemoteCommandFeedback
                        attempt={lastCommandAttempt}
                        history={commandAttemptHistory}
                        onRetry={powerRoutineBusy || launcherBusyKey ? undefined : retryLastCommand}
                      />
                      {isLandscape && <details className="phone-remote__power-options"><summary>Power options</summary>{renderPowerRoutinePanel()}</details>}
                      <div className="phone-remote__landscape-preview" aria-label="Landscape remote control">
                        {supportsPreviewSnapshot && previewImageUrl ? (
                          <img src={previewImageUrl} alt={`Live preview for ${device.name}`} className="phone-remote__landscape-image" />
                        ) : (
                          <div className="phone-remote__landscape-empty">
                            {supportsPreviewSnapshot ? snapshotMessage || 'Preview loading.' : 'Live preview is unavailable.'}
                          </div>
                        )}
                        <div className="phone-remote__landscape-controls" aria-label="Landscape navigation controls">
                          <div className="phone-remote__landscape-control-grid">
                            <div />
                            {renderLandscapeControl('nav_up', 'Up', <ChevronUp aria-hidden="true" />)}
                            <div />
                            {renderLandscapeControl('nav_left', 'Left', <ChevronLeft aria-hidden="true" />)}
                            {renderLandscapeControl('ok', 'OK', null, 'phone-remote__landscape-control--ok')}
                            {renderLandscapeControl('nav_right', 'Right', <ChevronRight aria-hidden="true" />)}
                            <div />
                            {renderLandscapeControl('nav_down', 'Down', <ChevronDown aria-hidden="true" />)}
                            <div />
                          </div>
                          <div className="phone-remote__landscape-bottom-controls">
                            {renderLandscapeControl('back', 'Back', <Undo2 aria-hidden="true" />, 'phone-remote__landscape-control--back')}
                            {renderLandscapeControl('home', 'Home', <Home aria-hidden="true" />, 'phone-remote__landscape-control--home')}
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="phone-remote__notice">Pick a target in settings.</div>
                  )}
                </>
              )}
            </div>
          </section>
        </div>
      </div>
    </RemotePwaSupport>
  );
}

export default RemoteApp;
